// ControlPanel - Layout, grouping, sorting, and color controls
import React, { useState, useMemo } from 'react';
import {
  LayoutType,
  PaletteType,
  ThemeType,
  SortDirection,
  SizeDimension,
  ShapeType,
} from '../../types/visualization';
import { THEMES, COLOR_PALETTES, LAYOUT_OPTIONS, isMobile } from '../../constants/visualizationThemes';

interface ControlPanelProps {
  layout: LayoutType;
  xAxis: string;
  sortColumn: string;
  sortDirection: SortDirection;
  colorColumn: string;
  colorPalette: PaletteType;
  searchQuery: string;
  onLayoutChange: (layout: LayoutType) => void;
  onXAxisChange: (column: string) => void;
  onSortChange: (column: string, direction: SortDirection) => void;
  onColorChange: (column: string) => void;
  onPaletteChange: (palette: PaletteType) => void;
  onSearchChange: (query: string) => void;
  searchMatchCount: number;
  columns: string[];
  columnCardinalities: Record<string, number>;
  theme: ThemeType;
  scatterXColumn: string;
  scatterYColumn: string;
  onScatterXChange: (column: string) => void;
  onScatterYChange: (column: string) => void;
  sizeByColumn: string | null;
  onSizeByColumnChange: (column: string | null) => void;
  sizeByDimension: SizeDimension;
  onSizeByDimensionChange: (dimension: SizeDimension) => void;
  maxSizeMultiplier: number;
  onMaxSizeChange: (multiplier: number) => void;
  shapeType: ShapeType;
  numericColumns: string[];
}

function Dropdown({ value, options, onChange, theme }: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  theme: ThemeType;
}) {
  const t = THEMES[theme];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        padding: '8px 10px',
        background: t.inputBg,
        border: `1px solid ${t.buttonBorder}`,
        borderRadius: '4px',
        color: t.text,
        fontSize: '12px',
        outline: 'none',
        cursor: 'pointer',
      }}
    >
      {options.map(opt => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

function Slider({ value, min, max, step, onChange, theme }: {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  theme: ThemeType;
}) {
  const t = THEMES[theme];
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      style={{
        width: '100%',
        height: '3px',
        background: t.buttonBorder,
        borderRadius: '2px',
        appearance: 'none',
        cursor: 'pointer',
      }}
    />
  );
}

export default function ControlPanel({
  layout,
  xAxis,
  sortColumn,
  sortDirection,
  colorColumn,
  colorPalette,
  onLayoutChange,
  onXAxisChange,
  onSortChange,
  onColorChange,
  onPaletteChange,
  columns,
  columnCardinalities,
  theme,
  scatterXColumn,
  scatterYColumn,
  onScatterXChange,
  onScatterYChange,
  sizeByColumn,
  onSizeByColumnChange,
  sizeByDimension,
  onSizeByDimensionChange,
  maxSizeMultiplier,
  onMaxSizeChange,
  shapeType,
  numericColumns = [],
}: ControlPanelProps) {
  const [collapsed, setCollapsed] = useState(() => isMobile());
  const t = THEMES[theme];

  const labelStyle: React.CSSProperties = {
    marginBottom: '8px',
    color: t.textMuted,
    fontSize: '10px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    fontWeight: 500,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const SegmentedButtons = ({ options, value, onChange, cols = 2 }: {
    options: { value: string; label: string }[];
    value: string;
    onChange: (value: string) => void;
    cols?: number;
  }) => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: '1px',
      background: t.buttonBorder,
      borderRadius: '4px',
      overflow: 'hidden',
    }}>
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          style={{
            padding: '7px 4px',
            background: value === opt.value ? t.accent : t.buttonBg,
            border: 'none',
            color: value === opt.value ? (theme === 'dark' ? '#000' : '#fff') : t.textMuted,
            fontSize: '10px',
            fontWeight: value === opt.value ? 600 : 400,
            cursor: 'pointer',
            transition: 'all 0.1s ease',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const groupableColumns = useMemo(() => {
    return [...columns].sort((a, b) => {
      const cardA = columnCardinalities[a] || 0;
      const cardB = columnCardinalities[b] || 0;
      return cardA - cardB;
    });
  }, [columns, columnCardinalities]);

  const scatterNumericColumns = useMemo(() => {
    return columns.filter(col => {
      const card = columnCardinalities[col];
      return card && card > 5;
    });
  }, [columns, columnCardinalities]);

  const layoutOptions = LAYOUT_OPTIONS.map(l => ({
    value: l,
    label: l.charAt(0).toUpperCase() + l.slice(1),
  }));

  const paletteOptions = Object.entries(COLOR_PALETTES).map(([key, { name }]) => ({
    value: key,
    label: name.slice(0, 4),
  }));

  return (
    <div style={{
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: t.panelBg,
      border: `1px solid ${t.panelBorder}`,
      borderRadius: '8px',
      color: t.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backdropFilter: 'blur(20px)',
      width: collapsed ? 'auto' : '220px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      maxHeight: 'calc(100vh - 40px)',
      overflowY: 'auto',
      transition: 'width 0.2s ease',
      zIndex: 100,
    }}>
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          cursor: 'pointer',
          borderBottom: collapsed ? 'none' : `1px solid ${t.panelBorder}`,
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: '11px', fontWeight: 600, color: t.textMuted, letterSpacing: '1px' }}>
          {collapsed ? '⚙' : 'CONTROLS'}
        </span>
        <span style={{ fontSize: '14px', color: t.textMuted, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
          ▼
        </span>
      </div>

      {!collapsed && (
        <div style={{ padding: '16px', paddingTop: '12px' }}>
          <div style={sectionStyle}>
            <div style={labelStyle}>Layout</div>
            <SegmentedButtons
              options={layoutOptions}
              value={layout}
              onChange={(v) => onLayoutChange(v as LayoutType)}
              cols={3}
            />
          </div>

          {layout === 'scatter' && (
            <>
              <div style={sectionStyle}>
                <div style={labelStyle}>X Axis</div>
                <Dropdown value={scatterXColumn} options={scatterNumericColumns} onChange={onScatterXChange} theme={theme} />
              </div>
              <div style={sectionStyle}>
                <div style={labelStyle}>Y Axis</div>
                <Dropdown value={scatterYColumn} options={scatterNumericColumns} onChange={onScatterYChange} theme={theme} />
              </div>
            </>
          )}

          <div style={sectionStyle}>
            <div style={labelStyle}>Group By</div>
            <Dropdown value={xAxis} options={groupableColumns} onChange={onXAxisChange} theme={theme} />
            <div style={{ marginTop: '4px', fontSize: '9px', color: t.textDimmed }}>
              {columnCardinalities[xAxis] || 0} unique values
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={labelStyle}>Sort By</div>
            <Dropdown
              value={sortColumn}
              options={['originalIndex', ...columns]}
              onChange={(val) => onSortChange(val, sortDirection)}
              theme={theme}
            />
            <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: t.buttonBorder, borderRadius: '4px', overflow: 'hidden' }}>
              <button
                onClick={() => onSortChange(sortColumn, 'asc')}
                style={{
                  padding: '7px',
                  background: sortDirection === 'asc' ? t.accent : t.buttonBg,
                  border: 'none',
                  color: sortDirection === 'asc' ? (theme === 'dark' ? '#000' : '#fff') : t.textMuted,
                  fontSize: '10px',
                  fontWeight: sortDirection === 'asc' ? 600 : 400,
                  cursor: 'pointer',
                }}
              >↑ ASC</button>
              <button
                onClick={() => onSortChange(sortColumn, 'desc')}
                style={{
                  padding: '7px',
                  background: sortDirection === 'desc' ? t.accent : t.buttonBg,
                  border: 'none',
                  color: sortDirection === 'desc' ? (theme === 'dark' ? '#000' : '#fff') : t.textMuted,
                  fontSize: '10px',
                  fontWeight: sortDirection === 'desc' ? 600 : 400,
                  cursor: 'pointer',
                }}
              >↓ DESC</button>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={labelStyle}>Color By</div>
            <Dropdown value={colorColumn} options={columns} onChange={onColorChange} theme={theme} />
          </div>

          <div style={sectionStyle}>
            <div style={labelStyle}>Palette</div>
            <SegmentedButtons
              options={paletteOptions}
              value={colorPalette}
              onChange={(v) => onPaletteChange(v as PaletteType)}
              cols={4}
            />
            <div style={{
              marginTop: '10px',
              height: '4px',
              borderRadius: '2px',
              background: COLOR_PALETTES[colorPalette].gradient,
            }} />
          </div>

          <div style={sectionStyle}>
            <div style={labelStyle}>Size By</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button
                onClick={() => onSizeByColumnChange(null)}
                style={{
                  padding: '7px 10px',
                  background: !sizeByColumn ? t.accent : t.buttonBg,
                  border: `1px solid ${t.buttonBorder}`,
                  borderRadius: '4px',
                  color: !sizeByColumn ? (theme === 'dark' ? '#000' : '#fff') : t.textMuted,
                  fontSize: '10px',
                  fontWeight: !sizeByColumn ? 600 : 400,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                None (uniform size)
              </button>
              {numericColumns.length > 0 && (
                <select
                  value={sizeByColumn || ''}
                  onChange={(e) => onSizeByColumnChange(e.target.value || null)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    background: t.inputBg,
                    border: `1px solid ${t.buttonBorder}`,
                    borderRadius: '4px',
                    color: t.text,
                    fontSize: '12px',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">Select numeric field...</option>
                  {numericColumns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {sizeByColumn && shapeType === 'cube' && (
            <div style={sectionStyle}>
              <div style={labelStyle}>Scale Axis</div>
              <SegmentedButtons
                options={[
                  { value: 'x', label: 'X' },
                  { value: 'y', label: 'Y' },
                  { value: 'z', label: 'Z' },
                ]}
                value={sizeByDimension}
                onChange={(v) => onSizeByDimensionChange(v as SizeDimension)}
                cols={3}
              />
            </div>
          )}

          {sizeByColumn && (
            <div style={{ marginBottom: 0 }}>
              <div style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between' }}>
                <span>Max Scale</span>
                <span style={{ color: t.textDimmed }}>{maxSizeMultiplier.toFixed(1)}x</span>
              </div>
              <Slider
                value={maxSizeMultiplier}
                min={1.5}
                max={6}
                step={0.5}
                onChange={onMaxSizeChange}
                theme={theme}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
